import React, { useState } from 'react';
import { createPackage, updatePackage, deletePackage } from './awsService';

export default function PackageManager({ packages, onCreated, onUpdated, onDeleted }) {
  const [editingId, setEditingId] = useState(null);
  const [name, setName] = useState('');
  const [drinks, setDrinks] = useState('');
  const [food, setFood] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const inputStyle = 'mt-2 w-full rounded-[18px] border border-[#203235] bg-[#0d1618] px-4 py-3 text-sm text-white focus:outline-none';
  const options = value => [...new Set(value.split('\n').map(s => s.trim()).filter(Boolean))];
  const reset = () => { setEditingId(null); setName(''); setDrinks(''); setFood(''); };
  const edit = pkg => {
    setEditingId(pkg.id); setName(pkg.name); setDrinks(pkg.drinkOptions.join('\n')); setFood(pkg.foodOptions.join('\n')); setMessage('');
  };
  const remove = async pkg => {
    if (!window.confirm(`Delete package "${pkg.name}"? Existing bookings will keep their assigned choices.`)) return;
    setBusy(true);
    try {
      await deletePackage(pkg.id); onDeleted(pkg.id);
      if (editingId === pkg.id) reset();
      setMessage('Package deleted. Existing bookings are unchanged.');
    } catch (error) { setMessage(`Failed to delete package: ${error.message}`); }
    finally { setBusy(false); }
  };
  const submit = async e => {
    e.preventDefault();
    setBusy(true);
    try {
      const data = { name: name.trim(), drinkOptions: options(drinks), foodOptions: options(food) };
      const pkg = editingId ? await updatePackage(editingId, data) : await createPackage(data);
      if (editingId) onUpdated(pkg); else onCreated(pkg);
      setMessage(editingId ? 'Package updated for all linked bookings. Submitted player choices are unchanged.' : 'Package created. It is now available when creating lead bookers.');
      reset();
    } catch (error) { setMessage(`Failed to save package: ${error.message}`); }
    finally { setBusy(false); }
  };
  return <section className="space-y-6">
    <form onSubmit={submit} className="rounded-[28px] border border-[#1f2f31] bg-[#111a1c] p-6 space-y-4">
      <h2 className="text-lg text-[#d3bf74]">{editingId ? 'Edit Package' : 'Create Package'}</h2>
      <p className="text-sm text-[#7d8f86]">Add one choice per line. Leave drinks or food blank if the package does not include them. Dietary requirements remain optional for every player. Edits update choices for all linked bookings. Submitted player choices are preserved. Deleting a package keeps its last choices on existing bookings.</p>
      <label className="block text-sm">Package name<input required maxLength={100} value={name} onChange={e => setName(e.target.value)} className={inputStyle} /></label>
      <div className="grid gap-4 md:grid-cols-2">
        <label className="block text-sm">Drink choices<textarea rows={5} value={drinks} onChange={e => setDrinks(e.target.value)} className={inputStyle} placeholder={'Sparkling wine\nElderflower pressé'} /></label>
        <label className="block text-sm">Food choices<textarea rows={5} value={food} onChange={e => setFood(e.target.value)} className={inputStyle} placeholder={'Margherita pizza\nVegan pizza'} /></label>
      </div>
      <button disabled={busy || !name.trim()} className="rounded-full bg-[#d3a23d] px-5 py-3 text-sm font-semibold text-black disabled:opacity-50">{busy ? 'Saving…' : editingId ? 'Save Changes' : 'Create Package'}</button>
      {editingId && <button type="button" disabled={busy} onClick={reset} className="ml-3 text-sm text-[#d3bf74]">Cancel</button>}
      {message && <p role="status" className="text-sm text-[#d3bf74]">{message}</p>}
    </form>
    <div className="grid gap-4 md:grid-cols-3">{packages.map(pkg => <article key={pkg.id} className="rounded-[24px] border border-[#1f2f31] bg-[#111a1c] p-6">
      <h3 className="text-lg font-semibold text-[#d3bf74]">{pkg.name}</h3>
      <p className="mt-3 text-sm">Drinks: {pkg.drinkOptions.join(', ') || 'Not included'}</p>
      <p className="mt-3 text-sm">Food: {pkg.foodOptions.join(', ') || 'Not included'}</p>
      <div className="mt-4 flex gap-4">
        <button disabled={busy} onClick={() => edit(pkg)} className="text-sm text-[#d3bf74] disabled:opacity-50">Edit</button>
        <button disabled={busy} onClick={() => remove(pkg)} className="text-sm text-red-400 disabled:opacity-50">Delete</button>
      </div>
    </article>)}</div>
  </section>;
}
